import {
  getAuth,
  initializeAuth,
  connectAuthEmulator,
  browserLocalPersistence,
  inMemoryPersistence,
  browserPopupRedirectResolver,
  onAuthStateChanged,
  signInWithRedirect,
  signInWithPopup,
  signInWithCredential,
  getRedirectResult,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendSignInLinkToEmail,
  sendPasswordResetEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  signInWithCustomToken,
  signOut,
  fetchSignInMethodsForEmail,
  updateEmail,
  sendEmailVerification,
  reload,
  updatePassword,
  updateProfile,
  applyActionCode,
  checkActionCode,
  verifyPasswordResetCode,
  confirmPasswordReset,
  EmailAuthProvider,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  GoogleAuthProvider,
  FacebookAuthProvider,
  OAuthProvider,
  type Auth,
  type AuthProvider,
  type User
} from 'firebase/auth'
import { getFirebaseApp } from './app'
import { requireProcessEnv, useFirebaseEmulator } from './env'

let authSingleton: Auth | null = null
let authEmulatorConnected = false

function maybeConnectAuthEmulator(auth: Auth): void {
  if (!useFirebaseEmulator() || authEmulatorConnected) return
  const host = requireProcessEnv('VUE_APP_FIREBASE_AUTH_EMULATOR_HOST')
  connectAuthEmulator(auth, `http://${host}`, { disableWarnings: true })
  authEmulatorConnected = true
}

/**
 * Browser 向け Auth。redirect SSO（Google/Apple/Facebook）には
 * popupRedirectResolver が必須。getAuth() 依存だとバンドラ次第で欠落することがあるため明示する。
 * Emulator（VUE_APP_USE_FIREBASE_EMULATOR=true）時は connectAuthEmulator を一度だけ呼ぶ。
 */
export function getFirebaseAuth(): Auth {
  if (authSingleton) {
    maybeConnectAuthEmulator(authSingleton)
    return authSingleton
  }

  const app = getFirebaseApp()
  const isBrowser = typeof window !== 'undefined'
  try {
    authSingleton = initializeAuth(app, {
      persistence: isBrowser ? browserLocalPersistence : inMemoryPersistence,
      ...(isBrowser
        ? { popupRedirectResolver: browserPopupRedirectResolver }
        : {})
    })
  } catch {
    // 既に初期化済み（HMR / 二重呼び出し）
    authSingleton = getAuth(app)
  }
  // Firebase 送信メール（認証メール等）とホスト型 /__/auth/action を日本語にする
  authSingleton.languageCode = 'ja'
  maybeConnectAuthEmulator(authSingleton)
  return authSingleton
}

export function createGoogleAuthProvider(): GoogleAuthProvider {
  return new GoogleAuthProvider()
}

export function createGoogleCredential(idToken: string) {
  return GoogleAuthProvider.credential(idToken)
}

export function createFacebookAuthProvider(): FacebookAuthProvider {
  return new FacebookAuthProvider()
}

export function createAppleAuthProvider(): OAuthProvider {
  const provider = new OAuthProvider('apple.com')
  provider.addScope('email')
  provider.setCustomParameters({ locale: 'ja' })
  return provider
}

export {
  onAuthStateChanged,
  signInWithRedirect,
  signInWithPopup,
  signInWithCredential,
  getRedirectResult,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendSignInLinkToEmail,
  sendPasswordResetEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  signInWithCustomToken,
  signOut,
  fetchSignInMethodsForEmail,
  updateEmail,
  sendEmailVerification,
  reload,
  updatePassword,
  updateProfile,
  applyActionCode,
  checkActionCode,
  verifyPasswordResetCode,
  confirmPasswordReset,
  EmailAuthProvider,
  reauthenticateWithCredential,
  reauthenticateWithPopup
}

export type { Auth, AuthProvider, User }
